import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { Building, CheckCircle2, XCircle } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

const THRESHOLDS = [
  { pct: 1.0, label: "1% Rule (Strict)" },
  { pct: 0.8, label: "0.8% Rule (Moderate)" },
  { pct: 0.5, label: "0.5% Rule (Minimum)" },
]

export default function OnePercentRuleCheckerPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [purchasePrice, setPurchasePrice] = useState("")
  const [monthlyRent, setMonthlyRent] = useState("")

  const results = useMemo(() => {
    const price = parseFloat(purchasePrice)
    const rent = parseFloat(monthlyRent)
    if (!price || !rent || price === 0) return null

    const ratio = (rent / price) * 100
    const gap = price * 0.01 - rent

    return { ratio, gap, price, rent }
  }, [purchasePrice, monthlyRent])

  const fmt = (n: number) => `${currency}${Math.round(n).toLocaleString()}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Property Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Purchase Price</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={purchasePrice}
                    onChange={e => setPurchasePrice(e.target.value)}
                    placeholder="e.g. 200000"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Expected Monthly Rent</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={monthlyRent}
                    onChange={e => setMonthlyRent(e.target.value)}
                    placeholder="e.g. 1000"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                <p className="text-xs text-blue-700 leading-relaxed">
                  The 1% rule states that monthly rent should be at least 1% of the purchase price. It is a quick screening tool — not a substitute for full due diligence.
                </p>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter purchase price and monthly rent</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Rent-to-Price Ratio</p>
                    <p className="text-5xl font-extrabold text-primary">{results.ratio.toFixed(3)}%</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-slate-500">
                          <th className="text-left pb-2">Threshold</th>
                          <th className="text-right pb-2">Required Rent</th>
                          <th className="text-right pb-2">Result</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {THRESHOLDS.map(({ pct, label }) => {
                          const required = results.price * (pct / 100)
                          const pass = results.rent >= required
                          return (
                            <tr key={pct}>
                              <td className="py-2 text-slate-700">{label}</td>
                              <td className="py-2 text-right text-slate-700">{fmt(required)}</td>
                              <td className="py-2 text-right">
                                {pass
                                  ? <span className="flex items-center justify-end gap-1 text-green-600 font-semibold"><CheckCircle2 className="w-4 h-4" /> Pass</span>
                                  : <span className="flex items-center justify-end gap-1 text-red-500 font-semibold"><XCircle className="w-4 h-4" /> Fail</span>
                                }
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {results.gap > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
                      You need <strong>{fmt(results.gap)}/month</strong> more to pass the strict 1% rule.
                    </div>
                  )}
                  {results.gap <= 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700">
                      This property passes the 1% rule — a strong screening result.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Rent-to-Price Ratio = Monthly Rent ÷ Purchase Price × 100\n\n1% Rule: Ratio ≥ 1.0% ✓\nModerate: Ratio ≥ 0.8% ✓\nMinimum: Ratio ≥ 0.5% ✓`}
        description="A ratio of 1% or more suggests the property may generate positive cash flow. Lower ratios are common in high-cost markets and do not automatically mean a bad investment."
      />

      <ToolFAQ
        items={[
          {
            q: "What is the 1% rule in real estate?",
            a: "The 1% rule is a quick screening metric that states monthly rent should be at least 1% of the purchase price. For example, a {currency}200,000 property should rent for at least {currency}2,000/month. It helps investors quickly filter out properties unlikely to generate positive cash flow.",
          },
          {
            q: "Is the 1% rule realistic in the UK?",
            a: "In most UK cities — especially London and the South East — the 1% rule is very difficult to achieve due to high property prices relative to rents. In more affordable areas (parts of the North, Midlands, Scotland), 0.6–0.8% is achievable. Use it as a starting screen, not a hard requirement.",
          },
          {
            q: "What should I do if a property fails the 1% rule?",
            a: "Failing the 1% rule doesn't automatically make a property a bad investment. High-value areas with strong appreciation may still generate excellent total returns through equity growth. Always follow up with a full cash flow analysis and ROI calculation before making a decision.",
          },
        ]}
      />

      <CTABox
        icon={Building}
        heading="Screening potential properties?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
